"use client";

import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Circle, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface LoaderState {
  text: string;
}

interface MultiStepLoaderProps {
  loading: boolean;
  loadingStates: LoaderState[];
  duration?: number;
  className?: string;
  textClassName?: string;
  indicatorClassName?: string;
  overlayClassName?: string;
  closeable?: boolean;
  persistUntilClose?: boolean;
  fullscreen?: boolean;
  lockScrollWhenVisible?: boolean;
  onClose?: () => void;
}

export function MultiStepLoader({
  loading,
  loadingStates,
  duration = 1800,
  className,
  textClassName,
  indicatorClassName,
  overlayClassName,
  closeable = false,
  persistUntilClose = false,
  fullscreen = false,
  lockScrollWhenVisible = false,
  onClose,
}: MultiStepLoaderProps) {
  const sanitizedStates = useMemo(
    () => loadingStates.filter((state) => state?.text?.trim().length > 0),
    [loadingStates]
  );

  const fallbackStates = useMemo<LoaderState[]>(
    () => [
      { text: "Preparing your experience" },
      { text: "Fetching latest data" },
      { text: "Almost there" },
    ],
    []
  );

  const statesToRender = sanitizedStates.length > 0 ? sanitizedStates : fallbackStates;
  const [activeStep, setActiveStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  useEffect(() => {
    if (loading) {
      setHasStartedLoading(true);
      setDismissed(false);
    }
  }, [loading]);

  useEffect(() => {
    if (!loading) {
      setActiveStep(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveStep((prevStep) => (prevStep + 1) % statesToRender.length);
    }, duration);

    return () => window.clearInterval(intervalId);
  }, [loading, duration, statesToRender.length]);

  const visible = !dismissed && (loading || (persistUntilClose && hasStartedLoading));

  useEffect(() => {
    if (!visible || !fullscreen || !lockScrollWhenVisible) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible, fullscreen, lockScrollWhenVisible]);

  if (!visible) {
    return null;
  }

  const handleClose = () => {
    setDismissed(true);
    onClose?.();
  };

  if (!fullscreen) {
    return (
      <div className={cn("flex w-full items-center justify-center", className)}>
        <div className="flex flex-col items-center gap-4 text-center">
          <div
            className={cn(
              "h-12 w-12 rounded-full border-2 border-[#8B31FF] border-t-transparent animate-spin",
              indicatorClassName
            )}
          />

          <AnimatePresence mode="wait">
            <motion.p
              key={`${statesToRender[activeStep]?.text}-${activeStep}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className={cn("text-sm font-medium", textClassName)}
            >
              {statesToRender[activeStep]?.text}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "fixed inset-0 z-[120] flex items-center justify-center overflow-hidden",
        overlayClassName
      )}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-[2px]" />

      {closeable && (
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 z-10 rounded-full p-2 text-white/90 transition hover:bg-white/10 hover:text-white"
          aria-label="Close loader"
        >
          <X className="h-6 w-6" />
        </button>
      )}

      <div className={cn("relative z-10 w-full max-w-md px-6", className)}>
        <div className="rounded-lg border border-white/10 bg-black/40 p-6">
          <div className="space-y-4">
            {statesToRender.map((state, index) => {
              const isCompleted = index < activeStep;
              const isActive = index === activeStep;

              return (
                <motion.div
                  key={`${state.text}-${index}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors",
                    isActive && "bg-[#8B31FF]/15"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-full border",
                      isActive && "border-[#8B31FF] bg-[#8B31FF] text-white",
                      isCompleted && "border-white/80 bg-white/90 text-black",
                      !isActive && !isCompleted && "border-white/40 text-white/70",
                      indicatorClassName
                    )}
                  >
                    {isActive || isCompleted ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Circle className="h-3.5 w-3.5" />
                    )}
                  </span>

                  <span
                    className={cn(
                      "text-lg font-medium text-white/70",
                      isActive && "text-[#C9A3FF]",
                      isCompleted && "text-white/90",
                      textClassName
                    )}
                  >
                    {state.text}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
